import React, { ReactElement } from 'react';

import { useTranslation } from 'react-i18next';
import { mergeClassNames, parseDynamicSlotDetails, stripLeadingZeroes, padTo6LeadingZeroes } from '../../../utils';

import { POG_OVERSTOCK, POG_DYNAMIC_SLOT, PLANO_ID_NOT_ON_PLANOGRAM } from '../../../constants';

import styles from './DetailsViewCardItemDetails.css';

type Props = {
  planogramName: string;
  planoID: string | null;
  sequence: string;
  partNumber: string;
  altPartNumber: string;
  sku: string;
};

const DetailsViewCardItemDetails = ({
  planogramName,
  planoID,
  sequence,
  partNumber,
  altPartNumber,
  sku,
}: Props): ReactElement => {
  const displayedPogFogZone =
    planogramName === POG_DYNAMIC_SLOT ? parseDynamicSlotDetails(sequence).zone : planogramName;
  const displayedSequence = planogramName === POG_DYNAMIC_SLOT ? parseDynamicSlotDetails(sequence).slot : sequence;

  const { t } = useTranslation();
  const translationRoot = 'ACTIVE_PICK_TOUR.DETAILS_VIEW';

  let resolvedPogFog = displayedPogFogZone || t(`${translationRoot}.NO_LOCATION_DATA`);
  if (planogramName === POG_OVERSTOCK) {
    resolvedPogFog = t(`${translationRoot}.OVERSTOCK`);
  } else if (planoID === PLANO_ID_NOT_ON_PLANOGRAM) {
    resolvedPogFog = t(`${translationRoot}.NOT_ON_PLANOGRAM`);
  }

  return (
    <article className={styles.article}>
      <header className={styles.header}>
        <div className={mergeClassNames(styles.leftContainer, planogramName === POG_DYNAMIC_SLOT ? styles.zone : '')}>
          <div className={styles.pogFogZone}>
            <p>{t(`${translationRoot}.${planogramName === POG_DYNAMIC_SLOT ? 'ZONE' : 'POG_FOG'}`)}</p>
            <h6 className={styles.pog} data-cy="pick-tour-item-pog-fog">
              {resolvedPogFog}
            </h6>
          </div>
          {planogramName === POG_DYNAMIC_SLOT ? (
            <div className={styles.aisle}>
              <p>{t(`${translationRoot}.AISLE`)}</p>
              <h6>{parseDynamicSlotDetails(sequence).aisle}</h6>
            </div>
          ) : null}
        </div>
        <div className={styles.slot}>
          <p>
            {t(
              `${translationRoot}.${
                [POG_OVERSTOCK, POG_DYNAMIC_SLOT].includes(planogramName)
                  ? 'SLOT'
                  : 'SEQ'
              }`
            )}
          </p>
          <h6 data-cy="pick-tour-item-seq">{displayedSequence}</h6>
        </div>
      </header>
      <section className={styles.section}>
        <p>{t(`${translationRoot}.PART_NUMBER`)}</p>
        <h1 data-cy="pick-tour-item-part-number">{partNumber}</h1>
      </section>
      <footer className={styles.footer}>
        <p>
          {t(`${translationRoot}.ALT_PART_NUMBER`)}:
          <span data-cy="pick-tour-item-alt-part-number">{altPartNumber}</span>
        </p>
        <p>
          {t(`${translationRoot}.SKU`)}:
          <span data-cy="pick-tour-item-sku-number">{padTo6LeadingZeroes(stripLeadingZeroes(sku))}</span>
        </p>
      </footer>
    </article>
  );
};

export default DetailsViewCardItemDetails;
